import type { Suggestion } from '../../aiSuggestionsPluginType';

/**
 * Returns the local ID of the node that the suggestion card should be anchored
 * to. Prefers the explicit `anchorLocalId` field on the suggestion when it is
 * present, and falls back to the `localId` from the first tool call's arguments
 * for backwards compatibility.
 */
export function getAnchorLocalId(suggestion: Suggestion): string | undefined {
	if (suggestion.anchorLocalId) {
		return suggestion.anchorLocalId;
	}
	const firstToolCall = suggestion.toolCalls?.[0];
	return firstToolCall && 'localId' in firstToolCall.arguments
		? firstToolCall.arguments.localId
		: undefined;
}

/**
 * Returns the local ID whose boundary should supply the bottom of the suggestion card. The card
 * still starts at `getAnchorLocalId`, but multi-call suggestions extend through their final edit.
 */
export function getAnchorEndLocalId(suggestion: Suggestion): string | undefined {
	const localIds = suggestion.action?.localIds;
	// for suggestions that supply localIds rather than toolcalls
	if (localIds?.length) {
		return localIds.at(-1);
	}

	const lastToolCall = suggestion.toolCalls?.at(-1);
	if (!lastToolCall || lastToolCall === suggestion.toolCalls?.at(0)) {
		return getAnchorLocalId(suggestion);
	}

	if ('localId' in lastToolCall.arguments) {
		return lastToolCall.arguments.localId;
	}

	return lastToolCall.arguments.localIds.at(-1) ?? suggestion.anchorLocalId;
}
